import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import { supabase } from 'utils/supabase'
import { mobileCss } from 'utils/theme'
import { Modal, Button, TextField, IconButton, LinearProgress, Accordion, AccordionSummary, AccordionDetails, Typography, Tooltip } from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import LinkOffIcon from '@mui/icons-material/LinkOff'
import AddLinkIcon from '@mui/icons-material/AddLink'
import MarkunreadMailboxOutlinedIcon from '@mui/icons-material/MarkunreadMailboxOutlined'
import AddIcon from '@mui/icons-material/Add'
import { toast } from 'react-toastify'
import { Delete as DeleteIcon, Edit as EditIcon, MailOutline as MailOutlineIcon, Link as LinkIcon, Autorenew as AutorenewIcon } from '@mui/icons-material'
import { ToastContainer } from 'react-toastify'
import { useNavigate } from 'react-router-dom'
import { v4 as uuidv4 } from 'uuid'
import 'react-toastify/dist/ReactToastify.css'
import { sendInvitation } from 'services/email'
import * as XLSX from 'xlsx'

type SendingDates = string[]

const ADDITIONAL_GUESTS_OPTIONS = [-1, 0, 1] as const
type AdditionalGuestsType = typeof ADDITIONAL_GUESTS_OPTIONS[number]

const GROUP_MAPPING = {
  kapitula: 'Kapituła',
  finalists: 'Finaliści',
  czlonkowiePRB: 'Członkowie PRB',
  BNGuest: 'BN Guest',
  PRBGuest: 'PRB Guest',
  vip: 'VIP',
  przewodniczacyK: 'Przewodniczący Kapituły',
  pwc: 'Partner Merytoryczny',
  rodzinaCz: 'Rodzina Czerneckich',
  rodzinaK: 'Rodzina Kulczyk',
  rodzinaW: 'Rodzina Wejchert',
  other: 'Inna'
} as const

type GroupKey = keyof typeof GROUP_MAPPING

interface InvitationType {
  id: string
  firstNameA: string
  lastNameA: string
  firstNameB: string | null
  lastNameB: string | null
  emailA: string
  emailB: string | null
  emailC: string | null
  emailD: string | null
  emailE: string | null
  stdLink: string | null
  paperInvitation: boolean
  stdSendingDates: SendingDates
  guestCategory: GroupKey
  stdInviteeCount: string
  stdAdditionalGuestsCount: string
  stdTotalGuestCount: string
  invitationStatus: InvitationStatus | null
  invitationSendingDates: SendingDates
  inviteeCount: string
  additionalGuestsCount: string
  totalInviteeCount: string
  addressNames?: string
  stdStatus: StdStatus | null
}

interface FilterState {
  paperInvitation: 'all' | 'true' | 'false'
  guestCategory: string
  stdStatus: StdStatus | 'all'
  invitationStatus: InvitationStatus | 'all'
}

type StdStatus =
  | 'STD weryfikacja danych przed wysyłką'
  | 'STD wysłane'
  | 'STD ponowione'
  | 'STD wymaga przypomnienia'
  | 'STD odmowa udziału'
  | 'STD zastępstwo'
  | 'STD potwierdzenie udziału'
  | 'STD brak odpowiedzi'

const STD_STATUS_OPTIONS: StdStatus[] = [
  'STD weryfikacja danych przed wysyłką',
  'STD wysłane',
  'STD ponowione',
  'STD wymaga przypomnienia',
  'STD odmowa udziału',
  'STD zastępstwo',
  'STD potwierdzenie udziału',
  'STD brak odpowiedzi'
]

type InvitationStatus =
  | 'Weryfikacja danych przed wysyłką'
  | 'Praca nad e-zaproszeniem'
  | 'Zaproszenie przekazane do kaligrafa'
  | 'Zaproszenie w trakcie dostawy kurierskiej'
  | 'E-zaproszenie wysłane'
  | 'E-zaproszenie wymaga przypomnienia'
  | 'E-zaproszenie ponowione'
  | 'Zaproszenie odmowa udziału'
  | 'Zaproszenie zastępstwo'
  | 'Zaproszenie potwierdzenie udziału'
  | 'Zaproszenie brak odpowiedzi'

const INVITATION_STATUS_OPTIONS: InvitationStatus[] = [
  'Weryfikacja danych przed wysyłką',
  'Praca nad e-zaproszeniem',
  'Zaproszenie przekazane do kaligrafa',
  'Zaproszenie w trakcie dostawy kurierskiej',
  'E-zaproszenie wysłane',
  'E-zaproszenie wymaga przypomnienia',
  'E-zaproszenie ponowione',
  'Zaproszenie odmowa udziału',
  'Zaproszenie zastępstwo',
  'Zaproszenie potwierdzenie udziału',
  'Zaproszenie brak odpowiedzi'
]

const STD_STATUS_WERYFIKACJA: StdStatus = 'STD weryfikacja danych przed wysyłką'

const isStdPotwierdzenieLubZastepstwo = (inv: InvitationType): boolean =>
  inv.stdStatus === 'STD potwierdzenie udziału' || inv.stdStatus === 'STD zastępstwo'

const hasInvitationStatusSet = (inv: InvitationType): boolean => inv.invitationStatus != null

const isInvPotwierdzenieLubZastepstwo = (inv: InvitationType): boolean =>
  inv.invitationStatus === 'Zaproszenie potwierdzenie udziału' ||
  inv.invitationStatus === 'Zaproszenie zastępstwo'

type SortField = 'lastNameA' | 'guestCategory' | 'stdStatus'
type SortDirection = 'asc' | 'desc'

const SearchBar = styled.div`
  display: flex;
  gap: 16px;
  flex: 1;
  flex-wrap: wrap;
  align-items: center;
  min-width: 0;
  
  .search-count {
    color: ${({ theme }) => theme.colors.text};
    font-size: 14px;
    white-space: nowrap;
    margin-left: auto;
  }
`
const SearchInput = styled.input`
  padding: 8px 16px;
  border: 1px solid #ddd;
  border-radius: 4px;
  flex: 1;
  min-width: 200px;
  max-width: 480px;
  font-size: 14px;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }
`

const SearchButton = styled(Button)`
  && {
    color: ${({ theme }) => theme.colors.text};
    border-color: ${({ theme }) => theme.colors.border};
    min-width: 100px;
    
    &:hover {
      background-color: ${({ theme }) => theme.colors.background};
      border-color: ${({ theme }) => theme.colors.primary};
    }
  }
`

const StickyHeader = styled.div`
  position: sticky;
  top: 0;
  background: white;
  z-index: 1000;
  border-bottom: 1px solid #f2f2f2;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  margin: -20px -20px 0 -20px;
  padding: 20px;
`

const FiltersArea = styled.div`
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
  align-items: flex-end;
`

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const FilterLabel = styled.label`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text};
`

const FilterSelect = styled.select`
  padding: 8px;
  border-radius: 4px;
  border: 1px solid #ddd;
`

const SearchToolbarRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
  width: 100%;
`

const ToolbarQuickActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px;
  margin-left: auto;
`

const AccordionStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 8px;
`

const ToolbarPanel = styled(Accordion)`
  box-shadow: none;
  border: 1px solid #eee;
  border-radius: 4px;

  &::before {
    display: none;
  }
`

const StatsGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 24px;
  align-items: flex-start;
`

const StatsGridItem = styled.div`
  flex: 1;
  min-width: 220px;
`

const StdStatsScroll = styled.div`
  overflow-x: auto;
  max-width: 100%;
  margin: 0 -8px;
  padding: 0 8px;

  table {
    min-width: max-content;
    font-size: 13px;
  }

  th {
    font-size: 11px;
    font-weight: 600;
    line-height: 1.25;
    max-width: 150px;
  }
`

const EmailsCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 200px;
  
  div {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`

const DatesCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 120px;
  margin: 0 auto;
  
  div {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    text-align: center;
  }
`

const ButtonsWrapper = styled.div`
  display: flex;
  gap: 16px;
  margin-bottom: 24px;
`

const ButtonsContainer = styled.div`
  display: flex;
  gap: 16px;
  margin-bottom: 20px;
`

const SummaryTable = styled.table`
  border-collapse: collapse;
  min-width: 100px;
  
  th, td {
    padding: 8px 8px;
    border: 2px solid #eee;
    font-size: 14px;
    text-align: center;
  }
  
  th {
    background-color: #f8f8f8;
    font-weight: 500;
    text-align: center;
  }

  td {
    vertical-align: middle;
  }

  td .percentage {
    font-size: 12px;
    opacity: 0.8;
  }
`

const SummaryCell = styled.td<{ type: 'tak' | 'nie' }>`
  background-color: ${({ type, theme }) => 
    type === 'tak' ? `${theme.colors.success}22` : `${theme.colors.error}22`
  };
  color: ${({ type, theme }) => 
    type === 'tak' ? theme.colors.success : theme.colors.error
  };
  font-weight: 500;
  gap: 4px;
  min-width: 40px;

  .percentage {
    font-size: 12px;
    opacity: 0.8;
  }
`

const HeaderSection = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
`

const BackButton = styled(Button)`
  && {
    color: ${({ theme }) => theme.colors.text};
    border-color: ${({ theme }) => theme.colors.border};
    
    &:hover {
      background-color: ${({ theme }) => theme.colors.background};
      border-color: ${({ theme }) => theme.colors.primary};
    }
  }
`

const ClearButton = styled(Button)`
  && {
    color: ${({ theme }) => theme.colors.text};
    border-color: ${({ theme }) => theme.colors.border};
    
    &:hover {
      background-color: ${({ theme }) => theme.colors.background};
      border-color: ${({ theme }) => theme.colors.primary};
    }
  }
`

const DeleteButton = styled(Button)`
  && {
    background-color: ${({ theme }) => theme.colors.error};
    color: white;
    
    &:hover {
      background-color: ${({ theme }) => theme.colors.error}dd;
    }
  }
`

const ActionsCell = styled.div`
  display: flex;
  gap: 8px;
`

const ModalContent = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: white;
  padding: 32px;
  border-radius: 8px;
  width: 90%;
  max-width: 1000px;
  max-height: 90vh;
  overflow-y: auto;

  h2 {
    margin-top: 0;
    color: ${({ theme }) => theme.colors.primary};
    margin-bottom: 24px;
  }
`

const ModalGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
  margin-bottom: 24px;
`

const ModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 16px;
  margin-top: 24px;
`

const ActionButton = styled(Button)`
  && {
    color: ${({ theme }) => theme.colors.text};
    border-color: ${({ theme }) => theme.colors.border};
    
    &:hover {
      background-color: ${({ theme }) => theme.colors.background};
      border-color: ${({ theme }) => theme.colors.primary};
    }
  }
`

const TableWrapper = styled.div`
  flex: 1;
  overflow: auto;
  margin-top: 20px;
  position: relative;
`

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  
  th, td {
    padding: 12px;
    text-align: left;
    border-bottom: 1px solid #eee;
  }

  th {
    background-color: #f8f8f8;
    font-weight: 500;
    position: sticky;
    top: 0;
    z-index: 1;
  }

  tbody tr:hover {
    background-color: #f5f5f5;
  }

  .highlight {
    background-color: ${({ theme }) => theme.colors.primary}22;
  }
`

const TableRow = styled.tr<{ stdStatus?: StdStatus | null; invitationStatus?: InvitationStatus | null }>`
  &:hover {
    background-color: #f5f5f5;
  }
  ${props => {
    if (props.invitationStatus === 'Zaproszenie przekazane do kaligrafa') {
      return 'background-color: #e3f2fd;'
    }
    if (props.invitationStatus === 'Zaproszenie potwierdzenie udziału') {
      return 'background-color: #fff9c4;'
    }
    if (props.stdStatus === 'STD potwierdzenie udziału') {
      return 'background-color: #e8f5e9;'
    }
    return ''
  }}
`

const SortableHeader = styled.th`
  cursor: pointer;
  user-select: none;
  
  &:hover {
    background-color: #f0f0f0;
  }

  .sort-icon {
    margin-left: 4px;
    opacity: 0.5;
  }
`

const ColumnTotal = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text}99;
  margin-top: 4px;
`

const FormSelect = styled.select`
  padding: 8px;
  border-radius: 4px;
  border: 1px solid #ddd;
  width: 100%;
`

const FormCheckbox = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  grid-column: 1 / -1;
`

const ActionsToolbar = styled.div`
  display: flex;
  gap: 16px;
  margin-bottom: 24px;
`

const ProgressWrapper = styled.div`
  margin: 20px 0;
  width: 100%;
`

const ProgressText = styled.div`
  text-align: center;
  margin-top: 8px;
  color: ${({ theme }) => theme.colors.text};
`

const ModalButtons = styled.div`
  display: flex;
  gap: 8px;
  justify-content: flex-end;
  margin-top: 16px;
`

const ProgressBar = styled.div<{ progress: number }>`
  width: 100%;
  height: 8px;
  background-color: #f0f0f0;
  border-radius: 4px;
  margin-top: 8px;
  overflow: hidden;

  &::before {
    content: '';
    display: block;
    width: ${props => props.progress}%;
    height: 100%;
    background-color: #4CAF50;
    transition: width 0.3s ease;
  }
`;

const Invitations = () => {
  const navigate = useNavigate()
  const [invitations, setInvitations] = useState<InvitationType[]>([])
  const [selectedIds, setSelectedIds] = useState<string[]>([])
  const [isAddModalOpen, setIsAddModalOpen] = useState(false)
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const [editingInvitation, setEditingInvitation] = useState<InvitationType | null>(null)
  const [sortField, setSortField] = useState<SortField>('lastNameA')
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc')
  const [filters, setFilters] = useState<FilterState>({
    paperInvitation: 'all',
    guestCategory: '',
    stdStatus: 'all',
    invitationStatus: 'all'
  })
  const [searchTerm, setSearchTerm] = useState('')
  const [currentMatchIndex, setCurrentMatchIndex] = useState(0)
  const [matchedRows, setMatchedRows] = useState<number[]>([])
  const [isActionsModalOpen, setIsActionsModalOpen] = useState(false)
  const [deleteModalInvitation, setDeleteModalInvitation] = useState<InvitationType | { count: number } | null>(null)
  const [isSending, setIsSending] = useState(false)
  const [sendingProgress, setSendingProgress] = useState(0)
  const [isSendModalOpen, setIsSendModalOpen] = useState(false)
  const [isGenerateLinksModalOpen, setIsGenerateLinksModalOpen] = useState(false)
  const [isDeletingLinks, setIsDeletingLinks] = useState(false)
  const [deleteLinksProgress, setDeleteLinksProgress] = useState(0)
  const [deleteLinksResult, setDeleteLinksResult] = useState<{ success: number; failed: number } | null>(null)
  const [generateLinksProgress, setGenerateLinksProgress] = useState(0)
  const [isDeleteLinksModalOpen, setIsDeleteLinksModalOpen] = useState(false)
  const [regeneratingLinkIds, setRegeneratingLinkIds] = useState<Record<string, boolean>>({})
  const [selectedInvitations, setSelectedInvitations] = useState<InvitationType[]>([]);

  const PRODUCTION_URL = 'https://nagroda.prb.pl'
  const LINK_BASE_URL = (process.env.REACT_APP_URL || PRODUCTION_URL).replace(/\/$/, '')

  useEffect(() => {
    fetchInvitations()
  }, [])

  const fetchInvitations = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession()
      const { data, error } = await supabase
        .from('invitations')
        .select('*')
        .throwOnError()
      
      if (error) {
        console.error('Error fetching invitations:', error)
        toast.error('Error fetching invitations: ' + error.message)
        return
      }
      
      if (data) {
        const normalizeDates = (rawDates: unknown): string[] => {
          try {
            if (!rawDates) return []
            if (Array.isArray(rawDates)) {
              return rawDates
                .filter((date): date is string => typeof date === 'string')
                .sort((a, b) => new Date(b).getTime() - new Date(a).getTime())
                .slice(0, 3)
            }
            if (typeof rawDates === 'object') {
              return Object.values(rawDates as Record<string, unknown>)
                .filter((date): date is string => typeof date === 'string')
                .sort((a, b) => new Date(b).getTime() - new Date(a).getTime())
                .slice(0, 3)
            }
            return []
          } catch {
            return []
          }
        }

        const processedData = data.map(invitation => {
          const stdSendingDates = normalizeDates(invitation.stdSendingDates)
          const invitationSendingDates = normalizeDates(invitation.invitationSendingDates)
          const stdInviteeCount = invitation.stdInviteeCount || calculateInviteesCount(invitation).toString()
          const stdAdditionalGuestsCount = invitation.stdAdditionalGuestsCount || '0'
          const stdTotalGuestCount = invitation.stdTotalGuestCount || calculateTotalGuests(
            calculateInviteesCount(invitation),
            invitation.stdAdditionalGuestsCount || '0'
          ).toString()
          const inviteeCount = invitation.inviteeCount || stdInviteeCount
          const additionalGuestsCount = invitation.additionalGuestsCount ?? stdAdditionalGuestsCount
          const totalInviteeCount = invitation.totalInviteeCount || calculateTotalGuests(
            parseInt(inviteeCount || '0'),
            additionalGuestsCount || '0'
          ).toString()

          return {
            ...invitation,
            stdLink: (invitation.stdLink ?? null) as string | null,
            guestCategory: invitation.guestCategory as GroupKey,
            paperInvitation: invitation.paperInvitation ?? true,
            stdInviteeCount,
            stdAdditionalGuestsCount,
            stdTotalGuestCount,
            stdSendingDates,
            invitationStatus: invitation.invitationStatus || null,
            invitationSendingDates,
            inviteeCount,
            additionalGuestsCount,
            totalInviteeCount
          }
        })
        setInvitations(processedData)
      }
    } catch (err) {
      console.error('Unexpected error while fetching invitations:', err)
      toast.error('Wystąpił nieoczekiwany błąd podczas pobierania zaproszeń')
    }
  }

  const getFilteredInvitations = () => {
    return invitations.filter((inv: InvitationType) => {
      const matchesPaper = filters.paperInvitation === 'all' || inv.paperInvitation === (filters.paperInvitation === 'true')
      const matchesGuestCategory = !filters.guestCategory || inv.guestCategory.includes(filters.guestCategory)
      const matchesStdStatus = filters.stdStatus === 'all' || inv.stdStatus === filters.stdStatus
      const matchesInvitationStatus =
        filters.invitationStatus === 'all' || inv.invitationStatus === filters.invitationStatus

      return matchesPaper && matchesGuestCategory && matchesStdStatus && matchesInvitationStatus
    })
  }

  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      const filteredInvitations = getFilteredInvitations();
      setSelectedIds(filteredInvitations.map((inv: InvitationType) => inv.id));
      setSelectedInvitations(filteredInvitations);
    } else {
      setSelectedIds([]);
      setSelectedInvitations([]);
    }
  };

  const handleSelectOne = (id: string) => {
    if (selectedIds.includes(id)) {
      setSelectedIds(selectedIds.filter((selectedId: string) => selectedId !== id));
      setSelectedInvitations(selectedInvitations.filter(inv => inv.id !== id));
    } else {
      const invitation = invitations.find(inv => inv.id === id);
      if (invitation) {
        setSelectedIds([...selectedIds, id]);
        setSelectedInvitations([...selectedInvitations, invitation]);
      }
    }
  };

  const calculateInviteesCount = (invitation: Partial<InvitationType>): number => {
    // Groups that always get 2 invitees
    if (invitation.guestCategory === 'kapitula' || invitation.guestCategory === 'finalists' || invitation.guestCategory === 'czlonkowiePRB') {
      return 2
    }
    // For all other groups, count is based on whether there's a second person
    return invitation.firstNameB ? 2 : 1
  }

  const calculateTotalGuests = (inviteesCount: number, additionalGuests: string): number => {
    return inviteesCount + (parseInt(additionalGuests) || 0)
  }

  const formatAddressNames = (invitation: Partial<InvitationType>): string => {
    const firstNameA = invitation.firstNameA?.trim() || '';
    const lastNameA = invitation.lastNameA?.trim() || '';
    const firstNameB = invitation.firstNameB?.trim() || '';
    const lastNameB = invitation.lastNameB?.trim() || '';
    
    const fullNameA = `${firstNameA} ${lastNameA}`.trim();
    const fullNameB = `${firstNameB} ${lastNameB}`.trim();
    
    if (!fullNameA) return '';
    // Only include 'oraz' and second name if both firstName AND lastName of person B exist
    if (!firstNameB || !lastNameB) return fullNameA;
    
    return `${fullNameA} oraz ${fullNameB}`;
  };

  const handleGenerateLinks = async () => {
    if (!isGenerateLinksModalOpen) {
      setIsGenerateLinksModalOpen(true);
      return;
    }

    try {
      const progressToast = toast.loading('Generowanie linków...');
      let successCount = 0;
      let failCount = 0;

      const invitationsNeedingLinks = invitations.filter(inv => !inv.stdLink);

      for (const [index, invitation] of invitationsNeedingLinks.entries()) {
        try {
          const uniqueId = Math.random().toString(36).substring(2, 10);
          const fullUrl = `${LINK_BASE_URL}/zaproszenie/${uniqueId}`;

          const { error } = await supabase
            .from('invitations')
            .update({ stdLink: fullUrl })
            .eq('id', invitation.id);

          if (error) {
            console.error('Error generating link:', error);
            failCount++;
          } else {
            successCount++;
          }

          const progress = Math.round(((index + 1) / invitationsNeedingLinks.length) * 100);
          setGenerateLinksProgress(progress);
          toast.update(progressToast, {
            render: `Generowanie linków: ${index + 1}/${invitationsNeedingLinks.length}`
          });
        } catch (err) {
          console.error('Error processing invitation:', err);
          failCount++;
        }
      }

      toast.dismiss(progressToast);
      if (successCount > 0) {
        toast.success(`Wygenerowano ${successCount} linków`);
      }
      if (failCount > 0) {
        toast.error(`Nie udało się wygenerować ${failCount} linków`);
      }

      setGenerateLinksProgress(0);
      await fetchInvitations();
    } catch (err) {
      console.error('Error generating links:', err);
      toast.error('Wystąpił błąd podczas generowania linków');
      setGenerateLinksProgress(0);
    } finally {
      setIsGenerateLinksModalOpen(false);
    }
  };

  const handleUpdateAddressNames = async () => {
    try {
      const progressToast = toast.loading('Aktualizacja zwrotów adresowych...');
      let successCount = 0;
      let failCount = 0;

      for (const [index, invitation] of invitations.entries()) {
        try {
          const updates = {
            addressNames: formatAddressNames(invitation)
          };

          const { error } = await supabase
            .from('invitations')
            .update(updates)
            .eq('id', invitation.id);

          if (error) {
            console.error('Error updating address names:', error);
            failCount++;
          } else {
            successCount++;
          }

          toast.update(progressToast, {
            render: `Aktualizacja: ${index + 1}/${invitations.length}`
          });
        } catch (err) {
          console.error('Error processing invitation:', err);
          failCount++;
        }
      }

      toast.dismiss(progressToast);
      if (successCount > 0) {
        toast.success(`Zaktualizowano ${successCount} zwrotów adresowych`);
      }
      if (failCount > 0) {
        toast.error(`Nie udało się zaktualizować ${failCount} zwrotów adresowych`);
      }

      await fetchInvitations();
    } catch (err) {
      console.error('Error updating address names:', err);
      toast.error('Wystąpił błąd podczas aktualizacji zwrotów adresowych');
    }
  };

  const handleSetAllInvitationsElectronic = async () => {
    if (invitations.length === 0) {
      toast.info('Brak zaproszeń do aktualizacji')
      return
    }

    const confirmed = window.confirm(
      'Ta operacja ustawi WSZYSTKIE zaproszenia jako elektroniczne, niezależnie od obecnego wyboru. Czy kontynuować?'
    )

    if (!confirmed) return

    try {
      const { error } = await supabase
        .from('invitations')
        .update({ paperInvitation: false })
        .not('id', 'is', null)

      if (error) {
        console.error('Error updating invitation versions to electronic:', error)
        toast.error('Nie udało się ustawić wersji elektronicznej dla wszystkich zaproszeń')
        return
      }

      toast.success('Wszystkie zaproszenia ustawiono jako elektroniczne')
      await fetchInvitations()
    } catch (err) {
      console.error('Unexpected error while bulk-updating invitation versions:', err)
      toast.error('Wystąpił błąd podczas masowej zmiany wersji zaproszeń')
    }
  }

  const handleAddInvitation = async (newInvitation: Partial<InvitationType>) => {
    try {
      const inviteesCount = calculateInviteesCount(newInvitation);
      // Generate a random string of 8-50 alphanumeric characters
      const uniqueId = Math.random().toString(36).substring(2, 10);
      const stdLink = `${LINK_BASE_URL}/zaproszenie/${uniqueId}`;
      const addressNames = formatAddressNames(newInvitation);
      
      const { data, error } = await supabase
        .from('invitations')
        .insert([{
          id: uuidv4(),
          firstNameA: newInvitation.firstNameA,
          lastNameA: newInvitation.lastNameA,
          firstNameB: newInvitation.firstNameB,
          lastNameB: newInvitation.lastNameB,
          emailA: newInvitation.emailA,
          emailB: newInvitation.emailB,
          emailC: newInvitation.emailC,
          emailD: newInvitation.emailD,
          emailE: newInvitation.emailE,
          guestCategory: newInvitation.guestCategory,
          stdLink,
          addressNames,
          paperInvitation: newInvitation.paperInvitation ?? false,
          stdSendingDates: [],
          stdInviteeCount: inviteesCount.toString(),
          stdAdditionalGuestsCount: '0',
          stdTotalGuestCount: inviteesCount.toString(),
          invitationStatus: null,
          invitationSendingDates: [],
          inviteeCount: inviteesCount.toString(),
          additionalGuestsCount: '0',
          totalInviteeCount: inviteesCount.toString(),
          stdStatus: newInvitation.stdStatus || null
        }]);
      
      if (error) {
        console.error('Error adding invitation:', error);
        toast.error('Nie udało się dodać osoby. ' + error.message);
        return;
      }
      
      toast.success('Pomyślnie dodano osobę');
      fetchInvitations();
      setIsAddModalOpen(false);
    } catch (err) {
      console.error('Exception while adding invitation:', err);
      toast.error('Wystąpił nieoczekiwany błąd podczas dodawania osoby');
    }
  };

  const handleEditInvitation = async (updatedInvitation: Partial<InvitationType>) => {
    if (!editingInvitation?.id) return

    try {
      let inviteesCount = calculateInviteesCount(updatedInvitation)
      let stdAdditionalGuestsCount = updatedInvitation.stdAdditionalGuestsCount || '0'
      let stdTotalGuestCount = '0'
      
      stdTotalGuestCount = calculateTotalGuests(inviteesCount, stdAdditionalGuestsCount).toString()
      
      // Update addressNames based on current names
      const addressNames = formatAddressNames(updatedInvitation)

      const { error } = await supabase
        .from('invitations')
        .update({
          firstNameA: updatedInvitation.firstNameA,
          lastNameA: updatedInvitation.lastNameA,
          firstNameB: updatedInvitation.firstNameB,
          lastNameB: updatedInvitation.lastNameB,
          emailA: updatedInvitation.emailA,
          emailB: updatedInvitation.emailB,
          emailC: updatedInvitation.emailC,
          emailD: updatedInvitation.emailD,
          emailE: updatedInvitation.emailE,
          guestCategory: updatedInvitation.guestCategory,
          paperInvitation: updatedInvitation.paperInvitation,
          stdStatus: updatedInvitation.stdStatus,
          invitationStatus: updatedInvitation.invitationStatus,
          invitationSendingDates: updatedInvitation.invitationSendingDates,
          inviteeCount: updatedInvitation.inviteeCount,
          additionalGuestsCount: updatedInvitation.additionalGuestsCount,
          totalInviteeCount: updatedInvitation.totalInviteeCount,
          addressNames,
          stdInviteeCount: inviteesCount.toString(),
          stdAdditionalGuestsCount,
          stdTotalGuestCount
        })
        .eq('id', editingInvitation.id)
      
      if (error) {
        console.error('Error updating invitation:', error)
        toast.error('Nie udało się zaktualizować rekordu: ' + error.message)
        return
      }
      
      toast.success('Pomyślnie zapisano zmiany')
      fetchInvitations()
      setIsEditModalOpen(false)
      setEditingInvitation(null)
    } catch (err) {
      console.error('Exception while updating invitation:', err)
      toast.error('Wystąpił nieoczekiwany błąd podczas aktualizacji rekordu')
    }
  }

  const handleDeleteInvitation = async (id: string) => {
    const invitation = invitations.find(inv => inv.id === id)
    if (!invitation) return
    setDeleteModalInvitation(invitation)
  }

  const confirmDelete = async () => {
    if (!deleteModalInvitation) return

    try {
      const { error } = await supabase
        .from('invitations')
        .delete()
        .eq('id', 'id' in deleteModalInvitation ? deleteModalInvitation.id : '')
      
      if (error) {
        console.error('Error deleting invitation:', error)
        toast.error('Nie udało się usunąć rekordu: ' + error.message)
        return
      }
      
      toast.success('Pomyślnie usunięto zaproszenie')
      setDeleteModalInvitation(null)
      fetchInvitations()
    } catch (err) {
      console.error('Exception while deleting invitation:', err)
      toast.error('Wystąpił nieoczekiwany błąd podczas usuwania rekordu')
    }
  }

  const handleDeleteSelected = async () => {
    if (!selectedIds.length) return
    
    if (!window.confirm(`Czy na pewno chcesz usunąć ${selectedIds.length} zaznaczonych rekordów?`)) {
      return
    }

    try {
      const { error } = await supabase
        .from('invitations')
        .delete()
        .in('id', selectedIds)
      
      if (error) {
        console.error('Error deleting invitations:', error)
        toast.error('Nie udało się usunąć rekordów: ' + error.message)
        return
      }
      
      toast.success('Pomyślnie usunięto rekordy')
      setSelectedIds([])
      fetchInvitations()
    } catch (err) {
      console.error('Exception while deleting invitations:', err)
      toast.error('Wystąpił nieoczekiwany błąd podczas usuwania rekordów')
    }
  }

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortDirection('asc')
    }
  }

  const getSortedInvitations = () => {
    const filtered = getFilteredInvitations()
    return [...filtered].sort((a, b) => {
      // Always sort by lastNameA within groups (A-Z)
      const lastNameCompare = a.lastNameA.toLowerCase().localeCompare(b.lastNameA.toLowerCase())

      switch (sortField) {
        case 'lastNameA':
          return sortDirection === 'asc' ? lastNameCompare : -lastNameCompare

        case 'guestCategory': {
          const groupA = GROUP_MAPPING[a.guestCategory as GroupKey].toLowerCase()
          const groupB = GROUP_MAPPING[b.guestCategory as GroupKey].toLowerCase()
          
          // If same group, sort by lastName A-Z
          if (groupA === groupB) {
            return lastNameCompare
          }
          // Sort by group name, direction affects only the group order
          return sortDirection === 'asc' ? groupA.localeCompare(groupB) : groupB.localeCompare(groupA)
        }
        case 'stdStatus': {
          const statusA = (a.stdStatus || '').toLowerCase()
          const statusB = (b.stdStatus || '').toLowerCase()
          if (statusA === statusB) return lastNameCompare
          return sortDirection === 'asc' ? statusA.localeCompare(statusB) : statusB.localeCompare(statusA)
        }

        default:
          return lastNameCompare
      }
    })
  }

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) return <span className="sort-icon">↕</span>
    return <span className="sort-icon">{sortDirection === 'asc' ? '↑' : '↓'}</span>
  }

  const clearSearch = () => {
    setSearchTerm('')
    setMatchedRows([])
    setCurrentMatchIndex(0)
    // Remove highlight from all rows
    document.querySelectorAll('tbody tr').forEach(row => {
      row.classList.remove('highlight')
    })
  }

  const handleSearch = () => {
    if (!searchTerm) {
      clearSearch()
      return
    }

    const matches: number[] = []
    getSortedInvitations().forEach((inv, index) => {
      const searchFields = [
        inv.firstNameA,
        inv.lastNameA,
        inv.firstNameB,
        inv.lastNameB
      ].map(field => field?.toLowerCase() || '')

      if (searchFields.some(field => field.includes(searchTerm.toLowerCase()))) {
        matches.push(index)
      }
    })

    setMatchedRows(matches)
    if (matches.length > 0) {
      setCurrentMatchIndex(0)
      scrollToMatch(matches[0])
    } else {
      toast.info('Nie znaleziono wyników')
    }
  }

  const scrollToNextMatch = () => {
    if (matchedRows.length === 0) return
    const nextIndex = (currentMatchIndex + 1) % matchedRows.length
    setCurrentMatchIndex(nextIndex)
    scrollToMatch(matchedRows[nextIndex])
  }

  const scrollToMatch = (rowIndex: number) => {
    const rows = document.querySelectorAll('tbody tr')
    if (rows[rowIndex]) {
      rows[rowIndex].scrollIntoView({ behavior: 'smooth', block: 'center' })
      rows[rowIndex].classList.add('highlight')
      setTimeout(() => rows[rowIndex].classList.remove('highlight'), 2000)
    }
  }

  const handleSendInvitations = async () => {
    if (!isSendModalOpen) {
      setIsSendModalOpen(true);
      return;
    }

    setIsSending(true);
    let successCount = 0;
    let failCount = 0;

    const selectedInvitations = invitations.filter((inv: InvitationType) => selectedIds.includes(inv.id));
    
    // Start mock progress
    setSendingProgress(0);
    const mockProgress = setInterval(() => {
      setSendingProgress((prev: number) => {
        if (prev >= 90) {
          clearInterval(mockProgress)
          return 90
        }
        return prev + 10
      });
    }, 1000);

    try {
      await Promise.all(selectedInvitations.map(async (invitation: InvitationType) => {
        if (!invitation.stdLink) {
          failCount++;
          return;
        }
        const emails = [
          invitation.emailA,
          invitation.emailB,
          invitation.emailC,
          invitation.emailD,
          invitation.emailE
        ].filter(Boolean) as string[];

        try {
          const success = await sendInvitation({
            nameA: invitation.firstNameA || '',
            lastNameA: invitation.lastNameA || '',
            firstNameB: invitation.firstNameB || undefined,
            lastNameB: invitation.lastNameB || undefined,
            emails: emails, // Pass all emails at once
            stdLink: invitation.stdLink,
            addressNames: invitation.addressNames || formatAddressNames(invitation)
          });

          if (success) {
            successCount++;
            
            // Update sending dates in Supabase
            const currentDate = new Date().toISOString();
            const sendingDates = [...(invitation.stdSendingDates || []), currentDate];

            await supabase
              .from('invitations')
              .update({
                stdSendingDates: sendingDates,
                stdStatus: 'STD wysłane'
              })
              .eq('id', invitation.id);
          } else {
            failCount++;
          }
        } catch (err) {
          console.error('Error sending invitation:', err);
          failCount++;
        }
      }));

      // Complete the progress bar
      clearInterval(mockProgress);
      setSendingProgress(100);

      if (successCount > 0) {
        toast.success(`Pomyślnie wysłano ${successCount} zaproszeń`);
        await fetchInvitations();
      }
      if (failCount > 0) {
        toast.error(`Nie udało się wysłać ${failCount} zaproszeń`);
      }
    } catch (err) {
      console.error('Error in send process:', err);
    } finally {
      setTimeout(() => {
        setIsSending(false);
        setSendingProgress(0);
        setIsSendModalOpen(false);
        setIsActionsModalOpen(false);
      }, 500);
    }
  };

  const handleDeleteLinks = async () => {
    try {
      setIsDeletingLinks(true)
      setDeleteLinksResult(null)
      const invitationsToUpdate = invitations.filter((inv: InvitationType) => inv.stdLink)
      const total = invitationsToUpdate.length
      let processed = 0
      let failed = 0

      for (const invitation of invitationsToUpdate) {
        try {
          const { error } = await supabase
            .from('invitations')
            .update({ stdLink: null })
            .eq('id', invitation.id)

          if (error) throw error
          processed++
          setDeleteLinksProgress((processed / total) * 100)
        } catch (err) {
          console.error('Error deleting link:', err)
          toast.error(`Błąd podczas usuwania linku dla ${invitation.firstNameA} ${invitation.lastNameA}`)
          failed++
        }
      }

      toast.success('Wszystkie linki zostały usunięte')
      setDeleteLinksProgress(100)
      setDeleteLinksResult({ success: processed, failed })
      await fetchInvitations()
    } catch (err) {
      console.error('Error in handleDeleteLinks:', err)
      toast.error('Wystąpił błąd podczas usuwania linków')
    } finally {
      setIsDeletingLinks(false)
    }
  }

  const getSummaryStats = (filtered: InvitationType[]) => {
    const pctOf = (count: number, denom: number) => (denom === 0 ? 0 : Math.round((count / denom) * 100))

    const totalRows = filtered.length
    const verificationCount = filtered.filter(inv => inv.stdStatus === STD_STATUS_WERYFIKACJA).length
    const denomAfterVerification = totalRows - verificationCount

    const stdNotVerification = filtered.filter(inv => inv.stdStatus !== STD_STATUS_WERYFIKACJA).length

    const countStd = (status: StdStatus) => filtered.filter(inv => inv.stdStatus === status).length

    const nWymaga = countStd('STD wymaga przypomnienia')
    const nOdmowa = countStd('STD odmowa udziału')
    const nZastepstwo = countStd('STD zastępstwo')
    const nPotwierdz = countStd('STD potwierdzenie udziału')
    const nBrak = countStd('STD brak odpowiedzi')

    const denomPotwierdZastep = filtered.filter(isStdPotwierdzenieLubZastepstwo).length

    const paperForConfirmed = filtered.filter(
      inv => inv.paperInvitation && isStdPotwierdzenieLubZastepstwo(inv)
    ).length
    const eForConfirmed = filtered.filter(
      inv => !inv.paperInvitation && isStdPotwierdzenieLubZastepstwo(inv)
    ).length

    const expectedGuestsStd = filtered
      .filter(isStdPotwierdzenieLubZastepstwo)
      .reduce((sum, inv) => {
        const invitees = parseInt(inv.stdInviteeCount || '0', 10) || 0
        const extra = parseInt(inv.stdAdditionalGuestsCount || '0', 10) || 0
        return sum + invitees + extra
      }, 0)

    const denomInvNotNull = filtered.filter(hasInvitationStatusSet).length
    const denomInvPapier = filtered.filter(
      inv => hasInvitationStatusSet(inv) && inv.paperInvitation
    ).length

    const countInv = (status: InvitationStatus) =>
      filtered.filter(inv => inv.invitationStatus === status).length

    const nEZapWyslane = countInv('E-zaproszenie wysłane')
    const nKaligraf = countInv('Zaproszenie przekazane do kaligrafa')
    const nKurier = countInv('Zaproszenie w trakcie dostawy kurierskiej')
    const nOdmowaInv = countInv('Zaproszenie odmowa udziału')
    const nZastepInv = countInv('Zaproszenie zastępstwo')
    const nPotwierdzInv = countInv('Zaproszenie potwierdzenie udziału')
    const nBrakInv = countInv('Zaproszenie brak odpowiedzi')

    const expectedGuestsInv = filtered
      .filter(isInvPotwierdzenieLubZastepstwo)
      .reduce((sum, inv) => {
        const invitees = parseInt(inv.inviteeCount || '0', 10) || 0
        const extra = parseInt(inv.additionalGuestsCount || '0', 10) || 0
        return sum + invitees + extra
      }, 0)

    return {
      std: {
        totalRows,
        stdWyslane: { count: stdNotVerification, pct: pctOf(stdNotVerification, totalRows) },
        wymagaPrzypomnienia: { count: nWymaga, pct: pctOf(nWymaga, denomAfterVerification) },
        odmowa: { count: nOdmowa, pct: pctOf(nOdmowa, denomAfterVerification) },
        zastepstwo: { count: nZastepstwo, pct: pctOf(nZastepstwo, denomAfterVerification) },
        potwierdzenie: { count: nPotwierdz, pct: pctOf(nPotwierdz, denomAfterVerification) },
        brakOdpowiedzi: { count: nBrak, pct: pctOf(nBrak, denomAfterVerification) },
        papierowePotwierdzone: {
          count: paperForConfirmed,
          pct: pctOf(paperForConfirmed, denomPotwierdZastep)
        },
        eZapPotwierdzone: {
          count: eForConfirmed,
          pct: pctOf(eForConfirmed, denomPotwierdZastep)
        },
        spodziewanaLiczbaGosci: expectedGuestsStd
      },
      invitation: {
        denomInvNotNull,
        denomInvPapier,
        eZapWyslane: { count: nEZapWyslane, pct: pctOf(nEZapWyslane, denomInvNotNull) },
        kaligraf: { count: nKaligraf, pct: pctOf(nKaligraf, denomInvPapier) },
        kurier: { count: nKurier, pct: pctOf(nKurier, denomInvNotNull) },
        odmowa: { count: nOdmowaInv, pct: pctOf(nOdmowaInv, denomInvNotNull) },
        zastepstwo: { count: nZastepInv, pct: pctOf(nZastepInv, denomInvNotNull) },
        potwierdzenie: { count: nPotwierdzInv, pct: pctOf(nPotwierdzInv, denomInvNotNull) },
        brakOdpowiedzi: { count: nBrakInv, pct: pctOf(nBrakInv, denomInvNotNull) },
        spodziewanaLiczbaGosci: expectedGuestsInv
      }
    }
  }

  const handleExport = (): boolean => {
    const selected = invitations.filter(inv => selectedIds.includes(inv.id));
    if (selected.length === 0) {
      toast.warning('Wybierz przynajmniej jeden rekord do eksportu');
      return false;
    }

    try {
      // Prepare data for export
      const exportData = selected.map(inv => ({
        'Imię i nazwisko osoby A': `${inv.firstNameA} ${inv.lastNameA}`,
        'Imię i nazwisko osoby B': inv.firstNameB ? `${inv.firstNameB} ${inv.lastNameB}` : '',
        'Wersja papierowa': inv.paperInvitation ? 'Tak' : 'Nie',
        'Liczba zaproszonych': inv.stdInviteeCount,
        'Status STD': inv.stdStatus || '',
        'Kategoria gościa': GROUP_MAPPING[inv.guestCategory as GroupKey] || inv.guestCategory,
        'Adresy email': [
          inv.emailA,
          inv.emailB,
          inv.emailC,
          inv.emailD,
          inv.emailE
        ].filter(Boolean).join(', ')
      }));

      // Create workbook and worksheet
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(exportData);
      
      // Add worksheet to workbook
      XLSX.utils.book_append_sheet(wb, ws, 'Zaproszenia');
      
      // Write file
      XLSX.writeFile(wb, 'zaproszenia.xlsx');
      toast.success('Pomyślnie wyeksportowano dane');
      return true;
    } catch (error) {
      console.error('Error during export:', error);
      toast.error('Wystąpił błąd podczas eksportu danych');
      return false;
    }
  };

  const handleExportAndMarkForCalligraphy = async () => {
    const selected = invitations.filter(inv => selectedIds.includes(inv.id))
    if (selected.length === 0) {
      toast.warning('Wybierz przynajmniej jeden rekord do eksportu')
      return
    }

    const exportSuccess = handleExport()
    if (!exportSuccess) return

    try {
      const { error } = await supabase
        .from('invitations')
        .update({ invitationStatus: 'Zaproszenie przekazane do kaligrafa' })
        .in('id', selectedIds)

      if (error) {
        toast.error('Nie udało się ustawić statusu "Zaproszenie przekazane do kaligrafa"')
        return
      }

      toast.success('Ustawiono status "Zaproszenie przekazane do kaligrafa" dla wybranych rekordów')
      await fetchInvitations()
      setIsActionsModalOpen(false)
    } catch (err) {
      console.error('Error during export and mark for calligraphy:', err)
      toast.error('Wystąpił błąd podczas aktualizacji statusu po eksporcie')
    }
  }

  const filteredInvitations = getFilteredInvitations()
  const summaryStats = getSummaryStats(filteredInvitations)
  const activeFilterCount = [
    filters.paperInvitation !== 'all',
    filters.guestCategory !== '',
    filters.stdStatus !== 'all',
    filters.invitationStatus !== 'all'
  ].filter(Boolean).length

  return (
    <Wrapper>
      <ToastContainer position="top-right" />
      <StickyHeader>
        <HeaderSection>
          <Title>Zaproszenia</Title>
          <BackButton 
            variant="outlined"
            onClick={() => navigate('/admin')}
          >
            Powrót
          </BackButton>
        </HeaderSection>

        <SearchToolbarRow>
          <SearchBar>
            <SearchInput
              placeholder="Szukaj po imieniu lub nazwisku..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value)
                if (!e.target.value) {
                  clearSearch()
                }
              }}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            />
            <SearchButton
              variant="outlined"
              onClick={handleSearch}
            >
              Szukaj
            </SearchButton>
            {searchTerm && (
              <SearchButton
                variant="outlined"
                onClick={clearSearch}
              >
                Wyczyść
              </SearchButton>
            )}
            {matchedRows.length > 0 && (
              <>
                <SearchButton
                  variant="outlined"
                  onClick={scrollToNextMatch}
                >
                  Następny ({currentMatchIndex + 1}/{matchedRows.length})
                </SearchButton>
                <span className="search-count">Znaleziono: {matchedRows.length}</span>
              </>
            )}
          </SearchBar>
          <ToolbarQuickActions>
            <Tooltip title="TEMP: Ustawia wszystkie rekordy jako wersja elektroniczna, niezależnie od wcześniejszego wyboru.">
              <span>
                <Button
                  variant="contained"
                  size="small"
                  color="warning"
                  onClick={handleSetAllInvitationsElectronic}
                  disabled={invitations.length === 0}
                >
                  TEMP: Ustaw WSZYSTKIE na elektroniczne
                </Button>
              </span>
            </Tooltip>
            <Tooltip title="Masowe akcje na zaznaczonych wierszach: eksport do Excela, zmiana statusu STD itd. Wymaga zaznaczenia co najmniej jednego rekordu.">
              <span>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => setIsActionsModalOpen(true)}
                  disabled={selectedIds.length === 0}
                >
                  Akcje
                </Button>
              </span>
            </Tooltip>
            <Tooltip
              title={
                generateLinksProgress > 0
                  ? `Trwa generowanie linków STD… ${Math.round(generateLinksProgress)}%`
                  : 'Generuj linki STD — otwiera okno, w którym wybierasz zakres (np. zaznaczeni lub wszyscy) i uruchamiasz generowanie unikalnych linków do formularza.'
              }
            >
              <span>
                <IconButton
                  size="small"
                  onClick={() => setIsGenerateLinksModalOpen(true)}
                  disabled={generateLinksProgress > 0}
                  sx={{ color: generateLinksProgress > 0 ? 'action.disabled' : '#2e7d32' }}
                  aria-label="Generuj linki STD"
                >
                  <AddLinkIcon />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Usuń wygenerowane linki STD z bazy — otwiera potwierdzenie. Dostępne tylko gdy istnieją rekordy z ustawionym linkiem.">
              <span>
                <IconButton
                  size="small"
                  onClick={() => setIsDeleteLinksModalOpen(true)}
                  disabled={invitations.filter((inv: InvitationType) => inv.stdLink).length === 0}
                  sx={{ color: '#c62828' }}
                  aria-label="Kasuj linki STD"
                >
                  <LinkOffIcon />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Aktualizuj zwroty adresowe (pan / pani / państwo) we wszystkich zaproszeniach na podstawie imion i płci — przelicza pola w bazie.">
              <span>
                <IconButton
                  size="small"
                  onClick={handleUpdateAddressNames}
                  disabled={invitations.length === 0}
                  color="primary"
                  aria-label="Aktualizuj zwroty adresowe"
                >
                  <MarkunreadMailboxOutlinedIcon />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Dodaj nowe zaproszenie — otwiera formularz tworzenia rekordu.">
              <IconButton
                size="small"
                onClick={() => setIsAddModalOpen(true)}
                sx={{
                  color: '#b8860b',
                  '&:hover': { backgroundColor: 'rgba(184, 134, 11, 0.08)' }
                }}
                aria-label="Dodaj zaproszenie"
              >
                <AddIcon />
              </IconButton>
            </Tooltip>
          </ToolbarQuickActions>
        </SearchToolbarRow>

        <AccordionStack>
          <ToolbarPanel defaultExpanded={false} disableGutters>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <div>
                <Typography component="div" variant="subtitle1" fontWeight={600}>
                  Filtry
                </Typography>
                <Typography variant="body2" color="text.secondary" component="div">
                  {filteredInvitations.length} rekordów
                  {activeFilterCount > 0 ? ` · ${activeFilterCount} aktywnych filtrów` : ''}
                </Typography>
              </div>
            </AccordionSummary>
            <AccordionDetails>
              <FiltersArea>
                <FilterGroup>
                  <FilterLabel>Wersja zaproszenia</FilterLabel>
                  <FilterSelect 
                    value={filters.paperInvitation} 
                    onChange={e => setFilters({...filters, paperInvitation: e.target.value as 'all' | 'true' | 'false'})}>
                    <option value="all">Wszystkie wersje</option>
                    <option value="true">Papierowe</option>
                    <option value="false">Elektroniczne</option>
                  </FilterSelect>
                </FilterGroup>

                <FilterGroup>
                  <FilterLabel>Kategoria gościa</FilterLabel>
                  <FilterSelect 
                    value={filters.guestCategory}
                    onChange={e => setFilters({...filters, guestCategory: e.target.value})}>
                    <option value="">Wszystkie grupy</option>
                    {Object.entries(GROUP_MAPPING).map(([key, label]) => (
                      <option key={key} value={key}>{label}</option>
                    ))}
                  </FilterSelect>
                </FilterGroup>

                <FilterGroup>
                  <FilterLabel>Status STD</FilterLabel>
                  <FilterSelect 
                    value={filters.stdStatus} 
                    onChange={e => setFilters({...filters, stdStatus: e.target.value as StdStatus | 'all'})}>
                    <option value="all">Wszystkie statusy</option>
                    {STD_STATUS_OPTIONS.map(status => (
                      <option key={status} value={status}>{status}</option>
                    ))}
                  </FilterSelect>
                </FilterGroup>

                <FilterGroup>
                  <FilterLabel>Status zaproszenia</FilterLabel>
                  <FilterSelect 
                    value={filters.invitationStatus} 
                    onChange={e => setFilters({...filters, invitationStatus: e.target.value as InvitationStatus | 'all'})}>
                    <option value="all">Wszystkie statusy</option>
                    {INVITATION_STATUS_OPTIONS.map(status => (
                      <option key={status} value={status}>{status}</option>
                    ))}
                  </FilterSelect>
                </FilterGroup>

                <FilterGroup>
                  <FilterLabel>&nbsp;</FilterLabel>
                  <ClearButton 
                    variant="outlined"
                    onClick={() => setFilters({
                      paperInvitation: 'all',
                      guestCategory: '',
                      stdStatus: 'all',
                      invitationStatus: 'all'
                    })}
                  >
                    Wyczyść filtry
                  </ClearButton>
                </FilterGroup>
              </FiltersArea>
            </AccordionDetails>
          </ToolbarPanel>

          <ToolbarPanel defaultExpanded={false} disableGutters>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <div>
                <Typography component="div" variant="subtitle1" fontWeight={600}>
                  Statystyki
                </Typography>
                <Typography variant="body2" color="text.secondary" component="div">
                  Podsumowanie dla aktualnie przefiltrowanej listy ({filteredInvitations.length} rekordów)
                </Typography>
              </div>
            </AccordionSummary>
            <AccordionDetails>
              <StatsGrid>
                <StatsGridItem style={{ flexBasis: '100%', width: '100%', minWidth: 0 }}>
                  <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>STD</Typography>
                  <StdStatsScroll>
                    <SummaryTable>
                      <thead>
                        <tr>
                          <th>STD wysłane¹</th>
                          <th>STD wymaga przypomnienia</th>
                          <th>STD odmowa udziału</th>
                          <th>STD zastępstwo</th>
                          <th>STD potwierdzenie udziału</th>
                          <th>STD brak odpowiedzi</th>
                          <th>Zaproszenia papierowe dla potwierdzonych STD²</th>
                          <th>E-zaproszenia dla potwierdzonych STD²</th>
                          <th>STD spodziewana liczba gości³</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td style={{ textAlign: 'center' }}>
                            {summaryStats.std.stdWyslane.count}
                            <br />
                            <span className="percentage">({summaryStats.std.stdWyslane.pct}%)</span>
                          </td>
                          <td style={{ textAlign: 'center' }}>
                            {summaryStats.std.wymagaPrzypomnienia.count}
                            <br />
                            <span className="percentage">({summaryStats.std.wymagaPrzypomnienia.pct}%)</span>
                          </td>
                          <td style={{ textAlign: 'center' }}>
                            {summaryStats.std.odmowa.count}
                            <br />
                            <span className="percentage">({summaryStats.std.odmowa.pct}%)</span>
                          </td>
                          <td style={{ textAlign: 'center' }}>
                            {summaryStats.std.zastepstwo.count}
                            <br />
                            <span className="percentage">({summaryStats.std.zastepstwo.pct}%)</span>
                          </td>
                          <td style={{ textAlign: 'center' }}>
                            {summaryStats.std.potwierdzenie.count}
                            <br />
                            <span className="percentage">({summaryStats.std.potwierdzenie.pct}%)</span>
                          </td>
                          <td style={{ textAlign: 'center' }}>
                            {summaryStats.std.brakOdpowiedzi.count}
                            <br />
                            <span className="percentage">({summaryStats.std.brakOdpowiedzi.pct}%)</span>
                          </td>
                          <td style={{ textAlign: 'center' }}>
                            {summaryStats.std.papierowePotwierdzone.count}
                            <br />
                            <span className="percentage">({summaryStats.std.papierowePotwierdzone.pct}%)</span>
                          </td>
                          <td style={{ textAlign: 'center' }}>
                            {summaryStats.std.eZapPotwierdzone.count}
                            <br />
                            <span className="percentage">({summaryStats.std.eZapPotwierdzone.pct}%)</span>
                          </td>
                          <td style={{ textAlign: 'center', fontWeight: 600 }}>
                            {summaryStats.std.spodziewanaLiczbaGosci}
                          </td>
                        </tr>
                      </tbody>
                    </SummaryTable>
                  </StdStatsScroll>
                  <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 1.5, lineHeight: 1.5 }}>
                    ¹ Licznik: wiersze, gdzie status STD ≠ „STD weryfikacja danych przed wysyłką”. Mianownik: suma wierszy (lista po filtrach, obecnie {summaryStats.std.totalRows}).
                    <br />
                    ² Liczniki: wersja zaproszenia (papier / e) przy statusie „STD potwierdzenie udziału” lub „STD zastępstwo”. Mianownik: liczba takich wierszy (potwierdzenie + zastępstwo).
                    <br />
                    ³ Suma (liczba zaproszonych STD + osoby dodatkowe STD) dla statusów potwierdzenie lub zastępstwo — wartość bezwzględna, bez mianownika.
                  </Typography>
                </StatsGridItem>
                <StatsGridItem style={{ flexBasis: '100%', width: '100%', minWidth: 0 }}>
                  <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>Zaproszenie</Typography>
                  <StdStatsScroll>
                    <SummaryTable>
                      <thead>
                        <tr>
                          <th>E-zaproszenie wysłane¹</th>
                          <th>Zaproszenie przekazane do kaligrafa²</th>
                          <th>Zaproszenie w trakcie dostawy kurierskiej</th>
                          <th>Zaproszenie odmowa udziału</th>
                          <th>Zaproszenie zastępstwo</th>
                          <th>Zaproszenie potwierdzenie udziału</th>
                          <th>Zaproszenie brak odpowiedzi</th>
                          <th>Spodziewana liczba gości³</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td style={{ textAlign: 'center' }}>
                            {summaryStats.invitation.eZapWyslane.count}
                            <br />
                            <span className="percentage">({summaryStats.invitation.eZapWyslane.pct}%)</span>
                          </td>
                          <td style={{ textAlign: 'center' }}>
                            {summaryStats.invitation.kaligraf.count}
                            <br />
                            <span className="percentage">({summaryStats.invitation.kaligraf.pct}%)</span>
                          </td>
                          <td style={{ textAlign: 'center' }}>
                            {summaryStats.invitation.kurier.count}
                            <br />
                            <span className="percentage">({summaryStats.invitation.kurier.pct}%)</span>
                          </td>
                          <td style={{ textAlign: 'center' }}>
                            {summaryStats.invitation.odmowa.count}
                            <br />
                            <span className="percentage">({summaryStats.invitation.odmowa.pct}%)</span>
                          </td>
                          <td style={{ textAlign: 'center' }}>
                            {summaryStats.invitation.zastepstwo.count}
                            <br />
                            <span className="percentage">({summaryStats.invitation.zastepstwo.pct}%)</span>
                          </td>
                          <td style={{ textAlign: 'center' }}>
                            {summaryStats.invitation.potwierdzenie.count}
                            <br />
                            <span className="percentage">({summaryStats.invitation.potwierdzenie.pct}%)</span>
                          </td>
                          <td style={{ textAlign: 'center' }}>
                            {summaryStats.invitation.brakOdpowiedzi.count}
                            <br />
                            <span className="percentage">({summaryStats.invitation.brakOdpowiedzi.pct}%)</span>
                          </td>
                          <td style={{ textAlign: 'center', fontWeight: 600 }}>
                            {summaryStats.invitation.spodziewanaLiczbaGosci}
                          </td>
                        </tr>
                      </tbody>
                    </SummaryTable>
                  </StdStatsScroll>
                  <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 1.5, lineHeight: 1.5 }}>
                    ¹ Licznik: wiersze ze statusem zaproszenia „E-zaproszenie wysłane”. Mianownik: wiersze z ustawionym statusem zaproszenia (≠ puste), obecnie {summaryStats.invitation.denomInvNotNull}.
                    <br />
                    ² Licznik: status „Zaproszenie przekazane do kaligrafa”. Mianownik: wiersze z ustawionym statusem zaproszenia oraz wybraną wersją papierową (papier na etapie STD), obecnie {summaryStats.invitation.denomInvPapier}.
                    <br />
                    ³ Suma (liczba zaproszonych + osoby dodatkowe) dla statusów „Zaproszenie potwierdzenie udziału” lub „Zaproszenie zastępstwo” — wartość bezwzględna, bez mianownika.
                    <br />
                    Pozostałe kolumny (kurier, odmowa, zastępstwo, potwierdzenie, brak odpowiedzi): licznik = dokładnie ten status; mianownik jak w¹ ({summaryStats.invitation.denomInvNotNull}).
                  </Typography>
                </StatsGridItem>
              </StatsGrid>
            </AccordionDetails>
          </ToolbarPanel>
        </AccordionStack>
      </StickyHeader>

      <TableWrapper>
        <Table>
          <thead>
            <tr>
              <th>
                <input 
                  type="checkbox" 
                  checked={selectedIds.length === getFilteredInvitations().length}
                  onChange={handleSelectAll}
                />
              </th>
              <th>Edytowanie</th>
              <SortableHeader onClick={() => handleSort('guestCategory')}>
                Kategoria gościa <SortIcon field="guestCategory" />
              </SortableHeader>
              <SortableHeader onClick={() => handleSort('lastNameA')}>
                Gość główny <SortIcon field="lastNameA" />
              </SortableHeader>
              <th>Osoba towarzysząca</th>
              <th>Adresy email</th>
              <th>Link</th>
              <th>Status STD</th>
              <th>Daty wysylki STD</th>
              <th>Liczba zaproszonych STD
                <ColumnTotal>
                  ({getFilteredInvitations().reduce((sum: number, inv: InvitationType) => sum + parseInt(inv.stdInviteeCount), 0)})
                </ColumnTotal>
              </th>
              <th>Osoby dodatkowe STD
                <ColumnTotal>
                  ({getFilteredInvitations().reduce((sum: number, inv: InvitationType) => sum + parseInt(inv.stdAdditionalGuestsCount || '0'), 0)})
                </ColumnTotal>
              </th>
              <th>Suma gosci STD
                <ColumnTotal>
                  ({getFilteredInvitations().reduce((sum: number, inv: InvitationType) => sum + parseInt(inv.stdTotalGuestCount), 0)})
                </ColumnTotal>
              </th>
              <th>Wybrana wersja zaproszenia imiennego</th>
              <th>Status zaproszenia</th>
              <th>Data wysylki zaproszenia</th>
              <th>Liczba zaproszonych
                <ColumnTotal>
                  ({getFilteredInvitations().reduce((sum: number, inv: InvitationType) => sum + parseInt(inv.inviteeCount || '0'), 0)})
                </ColumnTotal>
              </th>
              <th>Osoby dodatkowe
                <ColumnTotal>
                  ({getFilteredInvitations().reduce((sum: number, inv: InvitationType) => sum + parseInt(inv.additionalGuestsCount || '0'), 0)})
                </ColumnTotal>
              </th>
              <th>Suma Gosci
                <ColumnTotal>
                  ({getFilteredInvitations().reduce((sum: number, inv: InvitationType) => sum + parseInt(inv.totalInviteeCount || '0'), 0)})
                </ColumnTotal>
              </th>
            </tr>
          </thead>
          <tbody>
            {getSortedInvitations().map((invitation, index) => {
              const mainGuest = `${invitation.firstNameA} ${invitation.lastNameA}`
              const additionalGuest = invitation.firstNameB ? `${invitation.firstNameB} ${invitation.lastNameB}` : ''

              return (
              <TableRow 
                key={invitation.id} 
                className={matchedRows.includes(index) ? 'highlight' : ''}
                stdStatus={invitation.stdStatus}
                invitationStatus={invitation.invitationStatus}
              >
                <td>
                  <input 
                    type="checkbox"
                    checked={selectedIds.includes(invitation.id)}
                    onChange={() => handleSelectOne(invitation.id)}
                  />
                </td>
                <td>
                  <ActionsCell>
                    <IconButton
                      size="small"
                      onClick={() => {
                        setEditingInvitation(invitation)
                        setIsEditModalOpen(true)
                      }}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteInvitation(invitation.id)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </ActionsCell>
                </td>
                <td>{GROUP_MAPPING[invitation.guestCategory as GroupKey] || invitation.guestCategory}</td>
                <td>{mainGuest}</td>
                <td>{additionalGuest}</td>
                <td>
                  <EmailsCell>
                    {[
                      invitation.emailA,
                      invitation.emailB,
                      invitation.emailC,
                      invitation.emailD,
                      invitation.emailE
                    ]
                      .filter(email => email)
                      .map((email, index) => (
                        <div key={index}>{email}</div>
                      ))}
                  </EmailsCell>
                </td>
                <td>
                  <IconButton
                    size="small"
                    disabled={!invitation.stdLink}
                    onClick={async () => {
                      try {
                        await navigator.clipboard.writeText(invitation.stdLink || '')
                        toast.success('Skopiowano link')
                      } catch (err) {
                        toast.error('Nie udalo sie skopiowac linku')
                      }
                    }}
                    title="Kopiuj link"
                  >
                    <LinkIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    disabled={!!regeneratingLinkIds[invitation.id]}
                    onClick={async () => {
                      setRegeneratingLinkIds(prev => ({ ...prev, [invitation.id]: true }))
                      try {
                        const uniqueId = Math.random().toString(36).substring(2, 10)
                        const fullUrl = `${LINK_BASE_URL}/zaproszenie/${uniqueId}`

                        const { error } = await supabase
                          .from('invitations')
                          .update({ stdLink: fullUrl })
                          .eq('id', invitation.id)

                        if (error) {
                          toast.error('Nie udalo sie wygenerowac linku')
                        } else {
                          setInvitations((prevInvitations: InvitationType[]) =>
                            prevInvitations.map((inv: InvitationType) =>
                              inv.id === invitation.id ? { ...inv, stdLink: fullUrl } : inv
                            )
                          )
                          toast.success('Wygenerowano nowy link')
                        }
                      } catch (err) {
                        toast.error('Wystapil blad podczas generowania linku')
                      } finally {
                        setRegeneratingLinkIds(prev => {
                          const next = { ...prev }
                          delete next[invitation.id]
                          return next
                        })
                      }
                    }}
                    title="Wygeneruj nowy link"
                  >
                    <AutorenewIcon fontSize="small" />
                  </IconButton>
                </td>
                <td>
                  <select
                    value={invitation.stdStatus || ''}
                    onChange={async (e) => {
                      const rawStatus = e.target.value
                      const newStatus = rawStatus === '' ? null : rawStatus as StdStatus
                      try {
                        const { error } = await supabase
                          .from('invitations')
                          .update({ stdStatus: newStatus })
                          .eq('id', invitation.id)

                        if (error) {
                          toast.error('Nie udalo sie zaktualizowac statusu')
                        } else {
                          setInvitations((prevInvitations: InvitationType[]) =>
                            prevInvitations.map((inv: InvitationType) =>
                              inv.id === invitation.id ? { ...inv, stdStatus: newStatus } : inv
                            )
                          )
                        }
                      } catch (err) {
                        toast.error('Wystapil blad podczas aktualizacji statusu')
                      }
                    }}
                  >
                    <option value="">Wybierz status</option>
                    {STD_STATUS_OPTIONS.map(status => (
                      <option key={status} value={status}>{status}</option>
                    ))}
                  </select>
                </td>
                <td>
                  <DatesCell>
                    {(invitation.stdSendingDates || [])
                      .sort((dateA: string, dateB: string) => new Date(dateB).getTime() - new Date(dateA).getTime())
                      .map((date: string, index: number) => (
                        <div key={index}>{new Date(date).toLocaleDateString('pl-PL')}</div>
                      ))}
                  </DatesCell>
                </td>
                <td>{invitation.stdInviteeCount}</td>
                <td>
                  <select
                    value={invitation.stdAdditionalGuestsCount}
                    onChange={async (e) => {
                      const newValue = e.target.value
                      const inviteesCount = parseInt(invitation.stdInviteeCount)
                      const totalGuests = (inviteesCount + parseInt(newValue)).toString()
                      
                      try {
                        const { error } = await supabase
                          .from('invitations')
                          .update({
                            stdAdditionalGuestsCount: newValue,
                            stdTotalGuestCount: totalGuests
                          })
                          .eq('id', invitation.id)
                        
                        if (error) {
                          toast.error('Nie udało się zaktualizować liczby gości')
                        } else {
                          setInvitations((prevInvitations: InvitationType[]) =>
                            prevInvitations.map((inv: InvitationType) =>
                              inv.id === invitation.id
                                ? { ...inv, stdAdditionalGuestsCount: newValue, stdTotalGuestCount: totalGuests }
                                : inv
                            )
                          )
                          await fetchInvitations()
                        }
                      } catch (err) {
                        toast.error('Wystąpił błąd podczas aktualizacji liczby gości')
                      }
                    }}
                  >
                    {ADDITIONAL_GUESTS_OPTIONS.map(num => (
                      <option key={num} value={num}>{num}</option>
                    ))}
                  </select>
                </td>
                <td>{parseInt(invitation.stdInviteeCount) + parseInt(invitation.stdAdditionalGuestsCount || '0')}</td>
                <td>
                  <select
                    value={invitation.paperInvitation ? 'papierowa' : 'elektroniczna'}
                    onChange={async (e) => {
                      const paperInvitation = e.target.value === 'papierowa'
                      try {
                        const { error } = await supabase
                          .from('invitations')
                          .update({ paperInvitation })
                          .eq('id', invitation.id)

                        if (error) {
                          toast.error('Nie udalo sie zaktualizowac wersji zaproszenia')
                        } else {
                          setInvitations((prevInvitations: InvitationType[]) =>
                            prevInvitations.map((inv: InvitationType) =>
                              inv.id === invitation.id ? { ...inv, paperInvitation } : inv
                            )
                          )
                        }
                      } catch (err) {
                        toast.error('Wystapil blad podczas aktualizacji wersji zaproszenia')
                      }
                    }}
                  >
                    <option value="papierowa">Papierowa</option>
                    <option value="elektroniczna">Elektroniczna</option>
                  </select>
                </td>
                <td>
                  <select
                    value={invitation.invitationStatus || ''}
                    onChange={async (e) => {
                      const newStatus = (e.target.value || null) as InvitationStatus | null
                      try {
                        const { error } = await supabase
                          .from('invitations')
                          .update({ invitationStatus: newStatus })
                          .eq('id', invitation.id)

                        if (error) {
                          toast.error('Nie udalo sie zaktualizowac statusu zaproszenia')
                        } else {
                          setInvitations((prevInvitations: InvitationType[]) =>
                            prevInvitations.map((inv: InvitationType) =>
                              inv.id === invitation.id ? { ...inv, invitationStatus: newStatus } : inv
                            )
                          )
                        }
                      } catch (err) {
                        toast.error('Wystapil blad podczas aktualizacji statusu zaproszenia')
                      }
                    }}
                  >
                    <option value="">Wybierz status</option>
                    {INVITATION_STATUS_OPTIONS.map(status => (
                      <option key={status} value={status}>{status}</option>
                    ))}
                  </select>
                </td>
                <td>
                  <DatesCell>
                    <IconButton
                      size="small"
                      onClick={async () => {
                        const currentDate = new Date().toISOString()
                        const invitationSendingDates = [...(invitation.invitationSendingDates || []), currentDate]
                          .sort((a, b) => new Date(b).getTime() - new Date(a).getTime())
                          .slice(0, 3)

                        try {
                          const { error } = await supabase
                            .from('invitations')
                            .update({ invitationSendingDates })
                            .eq('id', invitation.id)

                          if (error) {
                            toast.error('Nie udalo sie dodac daty wysylki zaproszenia')
                          } else {
                            setInvitations((prevInvitations: InvitationType[]) =>
                              prevInvitations.map((inv: InvitationType) =>
                                inv.id === invitation.id ? { ...inv, invitationSendingDates } : inv
                              )
                            )
                          }
                        } catch (err) {
                          toast.error('Wystapil blad podczas zapisu daty wysylki zaproszenia')
                        }
                      }}
                    >
                      <MailOutlineIcon fontSize="small" />
                    </IconButton>
                    {(invitation.invitationSendingDates || [])
                      .sort((dateA: string, dateB: string) => new Date(dateB).getTime() - new Date(dateA).getTime())
                      .map((date: string, index: number) => (
                        <div key={index}>{new Date(date).toLocaleDateString('pl-PL')}</div>
                      ))}
                  </DatesCell>
                </td>
                <td>{invitation.inviteeCount}</td>
                <td>
                  <select
                    value={invitation.additionalGuestsCount}
                    onChange={async (e) => {
                      const newValue = e.target.value
                      const inviteeCount = parseInt(invitation.inviteeCount || '0')
                      const totalInviteeCount = (inviteeCount + parseInt(newValue || '0')).toString()

                      try {
                        const { error } = await supabase
                          .from('invitations')
                          .update({
                            additionalGuestsCount: newValue,
                            totalInviteeCount
                          })
                          .eq('id', invitation.id)

                        if (error) {
                          toast.error('Nie udalo sie zaktualizowac liczby gosci zaproszenia')
                        } else {
                          setInvitations((prevInvitations: InvitationType[]) =>
                            prevInvitations.map((inv: InvitationType) =>
                              inv.id === invitation.id
                                ? { ...inv, additionalGuestsCount: newValue, totalInviteeCount }
                                : inv
                            )
                          )
                        }
                      } catch (err) {
                        toast.error('Wystapil blad podczas aktualizacji liczby gosci zaproszenia')
                      }
                    }}
                  >
                    {ADDITIONAL_GUESTS_OPTIONS.map(num => (
                      <option key={num} value={num}>{num}</option>
                    ))}
                  </select>
                </td>
                <td>{parseInt(invitation.inviteeCount || '0') + parseInt(invitation.additionalGuestsCount || '0')}</td>
              </TableRow>
            )})}
          </tbody>
        </Table>
      </TableWrapper>

      <AddInvitationModal
        open={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onSubmit={handleAddInvitation}
      />

      {editingInvitation && (
        <AddInvitationModal
          open={isEditModalOpen}
          onClose={() => {
            setIsEditModalOpen(false)
            setEditingInvitation(null)
          }}
          onSubmit={handleEditInvitation}
          initialData={editingInvitation}
        />
      )}

      <Modal
        open={isActionsModalOpen}
        onClose={() => setIsActionsModalOpen(false)}
      >
        <ModalContent style={{ maxWidth: 800, padding: '32px' }}>
          <h2>Wybrane zaproszenia ({selectedIds.length})</h2>
          <InvitationList>
            {selectedInvitations.map(invitation => (
              <InvitationItem key={invitation.id}>
                {formatAddressNames(invitation)}
              </InvitationItem>
            ))}
          </InvitationList>
          <ActionButtons>
            <Button
              variant="outlined"
              onClick={() => {
                handleExport();
                setIsActionsModalOpen(false);
              }}
            >
              Eksport
            </Button>
            <Button
              variant="outlined"
              onClick={handleExportAndMarkForCalligraphy}
            >
              Eksport i przekazanie do wypisania
            </Button>
            <Button
              variant="outlined"
              onClick={() => {
                handleDeleteSelected();
                setIsActionsModalOpen(false);
              }}
            >
              Usuń zaznaczone
            </Button>
            <Button
              variant="outlined"
              onClick={() => {
                setIsSendModalOpen(true);
                setIsActionsModalOpen(false);
              }}
            >
              Wyślij zaproszenia
            </Button>
          </ActionButtons>
        </ModalContent>
      </Modal>

      <DeleteConfirmationModal
        open={!!deleteModalInvitation}
        onClose={() => setDeleteModalInvitation(null)}
        onConfirm={confirmDelete}
        invitation={deleteModalInvitation}
      />

      <Modal
        open={isGenerateLinksModalOpen}
        onClose={() => !generateLinksProgress && setIsGenerateLinksModalOpen(false)}
      >
        <ModalContent style={{ maxWidth: 500 }}>
          <h2>Potwierdź generowanie linków</h2>
          {!generateLinksProgress ? (
            <>
              <p style={{ marginBottom: 24 }}>
                Czy na pewno chcesz wygenerować linki dla wszystkich zaproszeń bez linków? Tej operacji nie można cofnąć.
              </p>
              <ModalButtons>
                <Button 
                  variant="outlined" 
                  onClick={() => setIsGenerateLinksModalOpen(false)}
                  disabled={generateLinksProgress > 0}
                >
                  Anuluj
                </Button>
                <Button
                  variant="contained"
                  color="error"
                  onClick={handleGenerateLinks}
                  disabled={generateLinksProgress > 0}
                >
                  Generuj linki
                </Button>
              </ModalButtons>
            </>
          ) : (
            <div>
              <p style={{ marginBottom: 16 }}>Generowanie linków...</p>
              <LinearProgress variant="determinate" value={generateLinksProgress} />
              <p style={{ marginTop: 8, textAlign: 'center' }}>
                {generateLinksProgress}%
              </p>
            </div>
          )}
        </ModalContent>
      </Modal>

      <Modal
        open={isDeleteLinksModalOpen}
        onClose={() => {
          if (isDeletingLinks) return
          setIsDeleteLinksModalOpen(false)
          setDeleteLinksProgress(0)
          setDeleteLinksResult(null)
        }}
      >
        <ModalContent style={{ maxWidth: 500 }}>
          <h2>Usuń wszystkie linki</h2>
          {!isDeletingLinks && deleteLinksResult ? (
            <>
              <p style={{ marginBottom: 24 }}>
                Usuwanie zakończone.
                <br />
                Usunięto: <b>{deleteLinksResult.success}</b>
                {deleteLinksResult.failed > 0 ? (
                  <>
                    <br />
                    Błędy: <b>{deleteLinksResult.failed}</b>
                  </>
                ) : null}
              </p>
              <ModalButtons>
                <Button
                  variant="contained"
                  onClick={() => {
                    setIsDeleteLinksModalOpen(false)
                    setDeleteLinksProgress(0)
                    setDeleteLinksResult(null)
                  }}
                >
                  Zamknij
                </Button>
              </ModalButtons>
            </>
          ) : !isDeletingLinks ? (
            <>
              <p style={{ marginBottom: 24 }}>
                Czy na pewno chcesz usunąć wszystkie linki z zaproszeń? Tej operacji nie można cofnąć.
              </p>
              <ModalButtons>
                <Button
                  variant="outlined"
                  onClick={() => {
                    setIsDeleteLinksModalOpen(false)
                    setDeleteLinksProgress(0)
                    setDeleteLinksResult(null)
                  }}
                  disabled={isDeletingLinks}
                >
                  Anuluj
                </Button>
                <Button
                  variant="contained"
                  color="error"
                  onClick={handleDeleteLinks}
                  disabled={isDeletingLinks}
                >
                  Usuń linki
                </Button>
              </ModalButtons>
            </>
          ) : (
            <div>
              <p style={{ marginBottom: 16 }}>Usuwanie linków...</p>
              <LinearProgress variant="determinate" value={deleteLinksProgress} />
              <p style={{ marginTop: 8, textAlign: 'center' }}>
                {Math.round(deleteLinksProgress)}%
              </p>
            </div>
          )}
        </ModalContent>
      </Modal>

      <Modal
        open={isSendModalOpen}
        onClose={() => !isSending && setIsSendModalOpen(false)}
      >
        <ModalContent style={{ maxWidth: 500 }}>
          <h2>Potwierdź wysyłkę zaproszeń</h2>
          {!isSending ? (
            <>
              <p style={{ marginBottom: 24 }}>
                Czy na pewno chcesz wysłać zaproszenia do {selectedInvitations.length} wybranych osób?
              </p>
              <ModalButtons>
                <Button 
                  variant="outlined" 
                  onClick={() => setIsSendModalOpen(false)}
                  disabled={isSending}
                >
                  Anuluj
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleSendInvitations}
                  disabled={isSending}
                >
                  Wyślij zaproszenia
                </Button>
              </ModalButtons>
            </>
          ) : (
            <div>
              <p style={{ marginBottom: 16 }}>Wysyłanie zaproszeń...</p>
              <LinearProgress variant="determinate" value={sendingProgress} />
              <p style={{ marginTop: 8, textAlign: 'center' }}>
                {sendingProgress}%
              </p>
            </div>
          )}
        </ModalContent>
      </Modal>
    </Wrapper>
  )
}

export default Invitations

interface AddInvitationModalProps {
  open: boolean
  onClose: () => void
  onSubmit: (invitation: Partial<InvitationType>) => void
  initialData?: InvitationType
}

const AddInvitationModal = ({ open, onClose, onSubmit, initialData }: AddInvitationModalProps) => {
  const defaultFormData: Partial<InvitationType> = {
    firstNameA: '',
    lastNameA: '',
    firstNameB: '',
    lastNameB: '',
    emailA: '',
    emailB: '',
    emailC: '',
    emailD: '',
    emailE: '',
    guestCategory: 'other' as GroupKey,
    paperInvitation: false,
    invitationStatus: null,
    invitationSendingDates: [],
    inviteeCount: '0',
    additionalGuestsCount: '0',
    totalInviteeCount: '0',
    stdStatus: null
  }

  const [formData, setFormData] = useState<Partial<InvitationType>>(initialData || defaultFormData)

  // Reset form when modal opens
  useEffect(() => {
    if (open) {
      setFormData(initialData || defaultFormData)
    }
  }, [open, initialData])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    onSubmit({ ...formData })
  }

  const handleTextChange = (field: keyof InvitationType) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setFormData(prev => ({ ...prev, [field]: e.target.value }))
  }

  const handleSelectChange = (field: keyof InvitationType) => (
    e: React.ChangeEvent<HTMLSelectElement>
  ) => {
    const value = e.target.value
    setFormData(prev => ({ ...prev, [field]: value }))
  }

  return (
    <Modal open={open} onClose={onClose}>
      <ModalContent>
        <h2>{initialData ? 'Edytuj zaproszenie' : 'Dodaj nowe osoby'}</h2>
        <form onSubmit={handleSubmit}>
          <ModalGrid>
            <TextField
              label="Imię A"
              value={formData.firstNameA}
              onChange={handleTextChange('firstNameA')}
              required
            />
            <TextField
              label="Nazwisko A"
              value={formData.lastNameA}
              onChange={handleTextChange('lastNameA')}
              required
            />
            <TextField
              label="Imię B"
              value={formData.firstNameB}
              onChange={handleTextChange('firstNameB')}
            />
            <TextField
              label="Nazwisko B"
              value={formData.lastNameB}
              onChange={handleTextChange('lastNameB')}
            />
            <TextField
              label="Email A"
              type="email"
              value={formData.emailA}
              onChange={handleTextChange('emailA')}
              required
            />
            <TextField
              label="Email B"
              type="email"
              value={formData.emailB}
              onChange={handleTextChange('emailB')}
            />
            <TextField
              label="Email C"
              type="email"
              value={formData.emailC}
              onChange={handleTextChange('emailC')}
            />
            <TextField
              label="Email D"
              type="email"
              value={formData.emailD}
              onChange={handleTextChange('emailD')}
            />
            <TextField
              label="Email E"
              type="email"
              value={formData.emailE}
              onChange={handleTextChange('emailE')}
            />
            <FormSelect
              value={formData.guestCategory as GroupKey}
              onChange={handleSelectChange('guestCategory')}
              required
            >
              {Object.entries(GROUP_MAPPING).map(([key, label]) => (
                <option key={key} value={key}>{label}</option>
              ))}
            </FormSelect>
            
            <FormCheckbox>
              <input
                type="checkbox"
                checked={formData.paperInvitation}
                onChange={(e) => setFormData((prev: Partial<InvitationType>) => ({ ...prev, paperInvitation: e.target.checked }))}
              />
              <label>Wersja papierowa</label>
            </FormCheckbox>
            <FormSelect
              value={formData.stdStatus || ''}
              onChange={handleSelectChange('stdStatus')}
            >
              <option value="">Wybierz status</option>
              {STD_STATUS_OPTIONS.map(status => (
                <option key={status} value={status}>{status}</option>
              ))}
            </FormSelect>
          </ModalGrid>
          <ModalActions>
            <Button variant="outlined" onClick={onClose}>
              Anuluj
            </Button>
            <Button variant="contained" type="submit">
              {initialData ? 'Zapisz' : 'Dodaj'}
            </Button>
          </ModalActions>
        </form>
      </ModalContent>
    </Modal>
  )
}

interface DeleteConfirmationModalProps {
  open: boolean
  onClose: () => void
  onConfirm: () => void
  invitation: InvitationType | { count: number } | null
}

const DeleteConfirmationModal = ({ open, onClose, onConfirm, invitation }: DeleteConfirmationModalProps) => {
  if (!invitation) return null

  const getMessage = () => {
    if ('count' in invitation) {
      return `Czy na pewno chcesz usunąć ${invitation.count} zaznaczonych rekordów?`
    }
    let name = `${invitation.firstNameA} ${invitation.lastNameA}`
    if (invitation.firstNameB) {
      name += ` i ${invitation.firstNameB} ${invitation.lastNameB}`
    }
    return `Czy na pewno chcesz usunąć zaproszenie dla: ${name}`
  }

  return (
    <Modal open={open} onClose={onClose}>
      <ModalContent style={{ maxWidth: 500 }}>
        <h2>Potwierdź usunięcie</h2>
        <p style={{ marginBottom: 24 }}>{getMessage()}</p>
        <ModalActions>
          <Button variant="outlined" onClick={onClose}>
            Anuluj
          </Button>
          <DeleteButton variant="contained" onClick={onConfirm}>
            Usuń
          </DeleteButton>
        </ModalActions>
      </ModalContent>
    </Modal>
  )
}

const Wrapper = styled.div`
  padding: 20px;
  min-width: 1200px;
  height: 100vh;
  display: flex;
  flex-direction: column;
  position: relative;
`

const Title = styled.h1`
  color: ${({ theme }) => theme.colors.primary};
`

const InvitationList = styled.div`
  max-height: 300px;
  overflow-y: auto;
  margin-bottom: 16px;
`

const InvitationItem = styled.div`
  padding: 8px;
  border-bottom: 1px solid #eee;
  &:last-child {
    border-bottom: none;
  }
`

const ActionButtons = styled.div`
  display: flex;
  gap: 8px;
  justify-content: flex-end;
  margin-top: 16px;
`

const ExportButton = styled(Button)`
  background-color: #4CAF50;
  color: white;
  &:hover {
    background-color: #45a049;
  }
`

const SendButton = styled(Button)`
  background-color: #4CAF50;
  color: white;
  &:hover {
    background-color: #45a049;
  }
`